import React, { useEffect, useMemo, useRef, useState } from "react";
import { getStoredChatId } from "./chatId";
import { useLocation, useNavigate } from "react-router-dom";
import { useCardStatement, cardIdFromSearch, cardQuery, cardIcon, formatMoney } from "./cardsStore";
import ImageWithCache from "./ImageWithCache";
import { isMonoTransferAvatar } from "./receiptUtils";
import { useMaskBalance } from "./balanceVisibility";
import { listView } from "./fxStore";
import "./History.css"

function formatDateLabel(dateStr) {
  const txDate = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  if (isSameDay(txDate, today)) return 'Сьогодні';
  if (isSameDay(txDate, yesterday)) return 'Вчора';

  const months = [
    'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
    'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'
  ];

  const day = txDate.getDate();
  const month = months[txDate.getMonth()];
  const year = txDate.getFullYear();

  // Як у monobank: «6 жовтня»; рік — лише для минулих років
  return year === today.getFullYear() ? `${day} ${month}` : `${day} ${month} ${year}`;
}

function groupTransactions(arr, currency) {
  const grouped = {};

  arr.forEach((tx) => {
    if (tx && tx.name && tx.amount && tx.img && tx.date) {
      // Групуємо за календарним днем, а не за підписом (він тепер без року)
      const d = new Date(tx.date);
      const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!grouped[dayKey]) grouped[dayKey] = [];

      grouped[dayKey].push({
        ...tx,
        formattedAmount: formatMoney(tx.amount, currency),
        dateObj: new Date(tx.date),
      });
    }
  });

  return Object.entries(grouped)
    .sort((a, b) => b[1][0].dateObj - a[1][0].dateObj)
    .map(([dayKey, items]) => ({
      key: dayKey,
      label: formatDateLabel(items[0].date),
      items: items.sort((a, b) => b.dateObj - a.dateObj),
    }));
}

const HISTORY_ANIM_MS = 300;

const TransactionsHistory = ({ asBackdrop = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const chatId = getStoredChatId();
  // Виписка тієї картки, з якої натиснули «Усі» (?card=<id>; без нього — основна)
  const cardId = cardIdFromSearch(location.search);
  const statement = useCardStatement(chatId, cardId);
  const transactions = useMemo(
    () => groupTransactions(statement.transactions, statement.currency),
    [statement.transactions, statement.currency]
  );
  const maskBalance = useMaskBalance();
  const balance = maskBalance(formatMoney(statement.balance, statement.currency));
  const headerIcon = (statement.card && cardIcon(statement.card.type)) || '/mini-mono.svg';

  // Дата групи «прилипає» одразу під шапкою з балансом (як у monobank), тож
  // їй потрібна висота шапки — міряємо, бо вона залежить від safe-area/шрифтів
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const measure = () => setHeaderHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Без слайд-ін анімації, якщо це фон під деталями транзакції (asBackdrop)
  // або ми повертаємось у вже відкриту історію із закритої транзакції
  // (прапорець виставляється в TransactionsDetails перед navigate(-1)).
  const [noAnim] = useState(() => {
    if (asBackdrop) return true;
    try {
      if (sessionStorage.getItem('skipHistoryAnim') === '1') {
        sessionStorage.removeItem('skipHistoryAnim');
        return true;
      }
    } catch (e) { /* ignore */ }
    return false;
  });

  // Закриття зі слайд-аутом (як avatar-modal-sheet): спершу програємо
  // анімацію вниз, потім реально повертаємось назад.
  const [closing, setClosing] = useState(false);
  const closeTimeoutRef = useRef(null);
  function handleClose() {
    if (closing || asBackdrop) return;
    setClosing(true);
    closeTimeoutRef.current = setTimeout(() => navigate(-1), HISTORY_ANIM_MS);
  }
  useEffect(() => () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
  }, []);

  // Історія лишається змонтованою під деталлю транзакції, тож після
  // повернення прапорець skipHistoryAnim ніхто не прочитав — прибираємо,
  // інакше наступне відкриття історії пройшло б без анімації.
  useEffect(() => {
    if (asBackdrop) return;
    try { sessionStorage.removeItem('skipHistoryAnim'); } catch (e) { /* ignore */ }
  }, [asBackdrop]);

  // Область під home-indicator (safe-area) фарбується фоном <body> (глобально
  // чорний), а сторінка історії — #1e1e1e, тож знизу лишалась чорна смуга.
  // Поки історія відкрита як основний екран — робимо body таким самим.
  // Для бекдропа (під деталлю транзакції) фон не чіпаємо — ним керує деталь.
  useEffect(() => {
    if (asBackdrop) return;
    const original = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#1e1e1e';
    return () => { document.body.style.backgroundColor = original; };
  }, [asBackdrop]);

  // Поки триває слайд-ін (transform на контейнері) — шапка не sticky, а
  // relative, щоб на iOS PWA іконки малювались одразу, а не із затримкою.
  const [animating, setAnimating] = useState(!noAnim);
  useEffect(() => {
    if (noAnim) return;
    const t = setTimeout(() => setAnimating(false), HISTORY_ANIM_MS);
    return () => clearTimeout(t);
  }, [noAnim]);

  useEffect(() => {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (metaViewport) {
      const originalContent = metaViewport.getAttribute('content');
      metaViewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1, viewport-fit=cover'
      );
  
      // Немного проскроллим вниз и сразу назад (1 мс задержка)
      const scrollTimeout = setTimeout(() => {
      window.scrollTo(0, 0); // прокрутка вверх
    }, 10);
  
      return () => {
        metaViewport.setAttribute('content', originalContent);
        clearTimeout(scrollTimeout);
      };
    }
  }, []);

  return (
    <div
      className={`transactions-history-page ${closing ? 'closing' : ''} ${noAnim ? 'no-anim' : ''} ${animating ? 'animating' : ''}`}
      style={{ '--history-header-h': `${headerHeight}px` }}
    >
      <div className="transaction-history-header" ref={headerRef}>
        <div onClick={handleClose} className="transaction-back-btn">
          <img src="/cross.png" alt="Cross" />
        </div>
        <div className="transaction-card-con">
          <img src={headerIcon} alt="Card" />
          <p className="transaction-balance-text">{balance}</p>
        </div>
        <div className="transaction-back-btn">
          <img src="/search.png" alt="Search" />
        </div>
      </div>
      <div className="transactions-elements-con-t">
  {transactions.length === 0 ? (
    <p>Немає транзакцій</p>
  ) : (
    transactions.map((group) => (
      <div className="transaction-group-con" key={group.key}>
        <p className="transaction-group-date">{group.label}</p>
        {group.items.map((tx) => (
          <div className="transaction-element" key={tx.id}
              data-id={tx.id}
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/transaction/${tx.id}${cardQuery(cardId)}`, { state: { from: 'history' } })}>
            <div className="transaction-name-t">
              <div className="transaction-img-con">
                <ImageWithCache className={`transaction-img-m ${isMonoTransferAvatar(tx) ? 'no-zoom' : ''}`} src={listView(tx).img} alt={tx.name} />
              </div>
              <p className="transaction-name-t">{listView(tx).name}</p>
            </div>
            <p className={`transaction-sum-m ${tx.type === 'income' ? 'income' : ''}`}>{maskBalance(tx.formattedAmount)}</p>
          </div>
        ))}
      </div>
    ))
  )}
</div>

    </div>
  );
};

export default TransactionsHistory;
